export const CELEBRATION_TEXTURE_KEY = 'celebration';
export const CELEBRATION_ANIM_KEY = 'fx.celebration.play';

export type CreateCelebrationOptions = {
  x: number;
  y: number;
  /** Sprite scale (defaults to 1). */
  scale?: number;
  /** Animation frameRate (defaults to 24). */
  frameRate?: number;
  /** Optional: initial animation repeat count to use when creating the animation definition. */
  repeat?: number;
  /** Set depth for the created sprite. */
  depth?: number;
  /** Set scrollFactor for the created sprite. */
  scrollFactor?: number;
};

export type PlayCelebrationOptions = {
  /** Animation repeat count. Use -1 to loop forever. Overrides the animation's default if provided. */
  repeat?: number;
  /** Animation time scale multiplier (defaults to 1). */
  timeScale?: number;
  /** Whether to destroy the sprite on animation complete (only meaningful when repeat !== -1). */
  autoDestroyOnComplete?: boolean;
};

let warnedMissingTexture = false;
let warnedMissingFrames = false;

/**
 * Call from a Scene's preload() to make the celebration atlas available.
 * Safe to call from multiple scenes.
 */
export function preloadCelebration(scene: Phaser.Scene): void {
  // If already loaded in TextureManager, nothing to do.
  if (scene.textures.exists(CELEBRATION_TEXTURE_KEY)) return;

  // If the loader is currently running, avoid queueing duplicates.
  // (Phaser will generally handle duplicates, but this keeps the queue clean.)

  if (
    Object.prototype.hasOwnProperty.call(
      scene.load.list,
      CELEBRATION_TEXTURE_KEY,
    )
  )
    return;

  scene.load.multiatlas(
    CELEBRATION_TEXTURE_KEY,
    'assets/animations/celebration.json',
    'assets/animations/',
  );
}

/**
 * Ensure the Phaser Animation exists. Must be called after the atlas is loaded.
 * Returns true if the animation exists (created or already present).
 */
export function ensureCelebrationAnimation(
  scene: Phaser.Scene,
  config?: { frameRate?: number; repeat?: number },
): boolean {
  if (scene.anims.exists(CELEBRATION_ANIM_KEY)) return true;

  if (!scene.textures.exists(CELEBRATION_TEXTURE_KEY)) {
    if (!warnedMissingTexture) {
      warnedMissingTexture = true;
      console.warn(
        `[CelebrationFx] Missing texture '${CELEBRATION_TEXTURE_KEY}'. Did you call preloadCelebration() in preload()?`,
      );
    }
    return false;
  }

  const frameNames = scene.textures
    .get(CELEBRATION_TEXTURE_KEY)
    .getFrameNames()
    .filter((n) => n.startsWith('celebration_') && n.endsWith('.png'))
    .sort((a, b) => {
      const na = Number(a.match(/_(\d+)\.png$/)?.[1] ?? 0);
      const nb = Number(b.match(/_(\d+)\.png$/)?.[1] ?? 0);
      return na - nb;
    });

  if (!frameNames.length) {
    if (!warnedMissingFrames) {
      warnedMissingFrames = true;
      console.warn(
        `[CelebrationFx] No frames found in '${CELEBRATION_TEXTURE_KEY}'. Expected names like celebration_0.png`,
      );
    }
    return false;
  }

  scene.anims.create({
    key: CELEBRATION_ANIM_KEY,
    frames: frameNames.map((frame) => ({
      key: CELEBRATION_TEXTURE_KEY,
      frame,
    })),
    frameRate: config?.frameRate ?? 24,
    repeat: config?.repeat ?? 0,
  });

  return true;
}

/**
 * Start the celebration animation on an existing sprite.
 * Returns false if the animation couldn't be ensured.
 */
export function playAnimation(
  scene: Phaser.Scene,
  sprite: Phaser.GameObjects.Sprite,
  options: PlayCelebrationOptions = {},
): boolean {
  const ok = ensureCelebrationAnimation(scene);
  if (!ok) return false;

  // If caller wants a different repeat count than the animation was created with,
  // Phaser supports overriding it per-play.
  sprite.play({
    key: CELEBRATION_ANIM_KEY,
    repeat: options.repeat,
  });

  sprite.setScale(0.8);

  if (options.timeScale !== undefined) {
    sprite.anims.timeScale = options.timeScale;
  }

  if (options.autoDestroyOnComplete && (options.repeat ?? -1) !== -1) {
    sprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () =>
      sprite.destroy(),
    );
  }

  return true;
}

/**
 * Create a celebration sprite.
 * NOTE: does NOT automatically play the animation. Call playCelebration(...) to start.
 * Returns null if assets/frames are missing.
 */
export function createCelebrationSprite(
  scene: Phaser.Scene,
  options: CreateCelebrationOptions,
): Phaser.GameObjects.Sprite | null {
  const ok = ensureCelebrationAnimation(scene, {
    frameRate: options.frameRate,
    repeat: options.repeat,
  });
  if (!ok) return null;

  const anim = scene.anims.get(CELEBRATION_ANIM_KEY);
  const firstFrame = anim?.frames?.[0]?.frame?.name;
  if (!firstFrame) return null;

  const sprite = scene.add.sprite(
    options.x,
    options.y,
    CELEBRATION_TEXTURE_KEY,
    firstFrame,
  );

  if (options.scale !== undefined) sprite.setScale(options.scale);
  if (options.depth !== undefined) sprite.setDepth(options.depth);
  if (options.scrollFactor !== undefined)
    sprite.setScrollFactor(options.scrollFactor);

  // Defensive cleanup if the Scene shuts down.
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    if (sprite.active) sprite.destroy();
  });

  return sprite;
}
